import cn from 'classnames';
import React, {useEffect, useState, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {useParams} from 'react-router-dom';

import {ListArrayFactory} from 'components/@common/specific/list-array-factory';
import {ConfirmModal} from 'components/confirm-modal/confirm-modal';
import {selectMetadata, ITableEntityMeta} from 'entities/metadata';
import {selectContextRawData} from 'modules/context/context-selector';
import {EEntityType} from 'shared/constants/entities';
import {useAppSelector} from 'store/config/hooks';
import {loadBranchNames, loadDocumentTypesOnUpload, uploadDocumentFiles} from 'store/slices/documents-uploading-slice';
import {selectIsThunkPending} from 'store/slices/loading-state-slice';

import {FileUploadModal} from '../components/file-upload-modal';
import {CONFIRM_MODAL_VIEWS, TConfirmModalViews} from './documents-file-upload-modal-utils';
import {FormsFileModalContext} from './file-modal.context';
import {useDocumentUploadActions} from './hooks/documents-upload-actions-hook';
import {useDocumentsUploadColumns} from './hooks/documents-upload-columns-hook';

import './document.less';

interface IDocumentsFileUploadModalContainerProps {
    onClose: () => void;
}

export const DocumentsFileUploadModalContainerComponent = ({onClose}: IDocumentsFileUploadModalContainerProps) => {
    const {entityName} = useParams<{ entityName: string }>();
    const dispatch = useDispatch();

    const isLoadingFiles = useAppSelector(s => selectIsThunkPending(s, uploadDocumentFiles.typePrefix));

    const contextRawData = useAppSelector(selectContextRawData);
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState<boolean>(false);
    const metadata = (useAppSelector(selectMetadata(entityName, EEntityType.EDITABLE_TABLE))) as ITableEntityMeta;

    const confirmModalViewRef = useRef<TConfirmModalViews>(CONFIRM_MODAL_VIEWS.XSD_VALIDATION);
    const duplicateFileNamesRef = useRef<string[]>([]);

    const {
        isLoading,
        useUploadDate,
        fileList,
        errorMsg,
        isPageJumperEnabled,
        isPageSizeChangerEnabled,
        actions,
        form,
        setForm,
        validation,
        setValidation,
        fileSizeFormat,
        fieldsFiltered,
        handleEdit,
        handleUpload,
        handleClear,
        handleDelete,
        handleSubmit,
        handleValidation,
        xsdValidationEnabled,
        filesWithError,
    } = useDocumentUploadActions(
      entityName,
      confirmModalViewRef,
      duplicateFileNamesRef,
      setIsConfirmModalVisible,
    );

    const {columnsMeta} = useDocumentsUploadColumns(
        entityName, fileSizeFormat, useUploadDate, handleDelete, handleEdit,
    );

    useEffect(() => {
        if (metadata && metadata?.useContext) {
            dispatch(loadBranchNames(true));
        }
        dispatch(loadDocumentTypesOnUpload());
    }, [contextRawData]);

    const confirmContent = {
      [CONFIRM_MODAL_VIEWS.XSD_VALIDATION]: {
        approveText: !xsdValidationEnabled ? 'Загрузить' : undefined,
        cancelText: !xsdValidationEnabled ? 'Отменить' : undefined,
        content: (
            <>
                <ListArrayFactory
                    listClassName={cn('scroll-list', 'list-height-limit')}
                    listArrayDescription={[
                        {
                            listTitle: 'Следующие файлы не прошли валидацию:',
                            list: filesWithError.map(file => ({
                                description: file.xsdValidationStatusLog === ''
                                    ? file.fileName
                                    : `${file.fileName} — ${file.xsdValidationStatusLog}`,
                            })),
                        },
                    ]}
                />
                {!xsdValidationEnabled && (
                    <div className={cn('d-flex', 'justify-content-center')}>
                        <h5 className="font-weight-bold">Вы уверены, что хотите сохранить их в системе?</h5>
                    </div>
                )}
            </>
    )},
    [CONFIRM_MODAL_VIEWS.DUPLICATE_VALIDATION]: {
      approveText: 'Да',
      cancelText: 'Нет',
      content:(
        <>
            <ListArrayFactory
                listClassName={cn('scroll-list', 'list-height-limit')}
                listArrayDescription={[
                    {
                        listTitle: 'Следующие документы уже были загружены:',
                        list: duplicateFileNamesRef.current.map(fileName => ({
                            description: fileName,
                        })),
                    },
                ]}
            />

            <div className={cn('d-flex', 'justify-content-center')}>
                <h5 className="font-weight-bold">
                    Вы уверены, что хотите продолжить загрузку?
                </h5>
            </div>
        </>
    ),
  }};

    return (
        <>
            <ConfirmModal
                title="Предупреждение"
                visible={isConfirmModalVisible}
                content={confirmContent[confirmModalViewRef.current]?.content}
                approveText={confirmContent[confirmModalViewRef.current]?.approveText}
                cancelText={confirmContent[confirmModalViewRef.current]?.cancelText}
                onCancel={() => setIsConfirmModalVisible(p => !p)}
                onConfirm={handleSubmit}
                isConfirmDisabled={isLoadingFiles}
            />

            <FormsFileModalContext.Provider
                value={{
                    form,
                    setForm,
                    validation,
                    setValidation,
                }}
            >
                <FileUploadModal
                    rowKey="fileName"
                    onClose={onClose}
                    onClear={handleClear}
                    onSubmit={handleValidation}
                    handleEdit={handleEdit}
                    modalTitle="Загрузка документов"
                    onUpload={handleUpload}
                    columns={columnsMeta}
                    columnsFiltered={fieldsFiltered}
                    fileList={fileList}
                    errorMsg={errorMsg}
                    actions={actions}
                    isPageJumperEnabled={isPageJumperEnabled}
                    isPageSizeChangerEnabled={isPageSizeChangerEnabled}
                    isLoading={isLoading}
                />
            </FormsFileModalContext.Provider>
        </>
    );
};

export const DocumentsFileUploadModalContainer = DocumentsFileUploadModalContainerComponent;
